import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  Calendar,
  MessageCircle,
  DollarSign,
  TrendingUp,
  Star,
  Video,
  Clock,
  BarChart3,
  Settings,
  Wallet,
  UserPlus,
  ArrowRight,
  Loader,
  CheckCircle,
  XCircle,
  ChevronRight,
} from "lucide-react";
import { mentorshipApi, showError } from "../lib";

const MentorDashboardPage = () => {
  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalSessions: 0,
    totalEarnings: 0,
    rating: 0,
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      const [sessionsResult, statsResult] = await Promise.all([
        mentorshipApi.getMySessions({ role: "mentor", limit: 10 }),
        mentorshipApi.getMentorStats(),
      ]);

      if (sessionsResult.success) {
        setSessions(sessionsResult.data);
      }

      if (statsResult.success && statsResult.data) {
        setStats({
          totalStudents: statsResult.data.totalMentees || 0,
          totalSessions: statsResult.data.totalSessions || 0,
          totalEarnings: statsResult.data.totalEarnings || 0,
          rating: statsResult.data.averageRating || 0,
        });
      }
    } catch (err: any) {
      console.error("Failed to fetch dashboard data:", err);
    } finally {
      setLoading(false);
    }
  };

  const completedSessions = sessions.filter((s) => s.status === "completed");
  const pendingSessions = sessions.filter((s) => s.status === "pending");
  const upcomingSessions = sessions.filter((s) => s.status === "confirmed");

  const quickLinks = [
    {
      label: "Manage Profile",
      icon: Settings,
      href: "/dashboard/mentor/profile",
      color: "bg-blue-500",
    },
    {
      label: "View Schedule",
      icon: Calendar,
      href: "/dashboard/mentor/schedule",
      color: "bg-green-500",
    },
    {
      label: "Messages",
      icon: MessageCircle,
      href: "/dashboard/mentor/messages",
      color: "bg-purple-500",
    },
    {
      label: "Earnings",
      icon: Wallet,
      href: "/dashboard/mentor/earnings",
      color: "bg-amber-500",
    },
  ];

  return (
    <div className="min-h-screen py-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30 md:py-6">
      <div className="px-4 mx-auto max-w-7xl md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-1">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 shadow-sm">
              <TrendingUp className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-lg font-bold text-gray-900 md:text-xl">
              Mentor Dashboard
            </h1>
          </div>
          <p className="text-[10px] text-gray-500 ml-10 md:text-xs">
            Welcome back! Here's your mentorship overview.
          </p>
        </div>

        {/* Stats Cards - Responsive grid */}
        <div className="grid grid-cols-2 gap-3 mb-5 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
          {/* Stat Card 1 */}
          <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-4 h-4 text-blue-600" />
              </div>
              <TrendingUp className="w-3 h-3 text-green-500" />
            </div>
            <p className="text-base font-bold text-gray-900 md:text-lg">
              {stats.totalStudents}
            </p>
            <p className="text-[9px] text-gray-500 md:text-[10px]">Total Students</p>
          </div>

          {/* Stat Card 2 */}
          <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <Video className="w-4 h-4 text-green-600" />
              </div>
              <Clock className="w-3 h-3 text-gray-400" />
            </div>
            <p className="text-base font-bold text-gray-900 md:text-lg">
              {stats.totalSessions}
            </p>
            <p className="text-[9px] text-gray-500 md:text-[10px]">Total Sessions</p>
          </div>

          {/* Stat Card 3 */}
          <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-4 h-4 text-amber-600" />
              </div>
            </div>
            <p className="text-base font-bold text-gray-900 md:text-lg">
              {new Intl.NumberFormat("fr-CM", {
                style: "currency",
                currency: "XAF",
                maximumFractionDigits: 0,
              }).format(stats.totalEarnings)}
            </p>
            <p className="text-[9px] text-gray-500 md:text-[10px]">Total Earnings</p>
          </div>

          {/* Stat Card 4 */}
          <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <Star className="w-4 h-4 text-purple-600" />
              </div>
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-2.5 h-2.5 ${star <= Math.round(stats.rating) ? "text-amber-400 fill-amber-400" : "text-gray-300"}`}
                  />
                ))}
              </div>
            </div>
            <p className="text-base font-bold text-gray-900 md:text-lg">
              {stats.rating > 0 ? stats.rating.toFixed(1) : "New"}
            </p>
            <p className="text-[9px] text-gray-500 md:text-[10px]">Average Rating</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <h2 className="text-xs font-semibold text-gray-900 mb-3 md:text-sm">
              Quick Actions
            </h2>
            <div className="space-y-1.5">
              {quickLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.href}
                  className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div
                    className={`w-8 h-8 ${link.color} rounded-lg flex items-center justify-center shrink-0`}
                  >
                    <link.icon className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-[11px] font-medium text-gray-900 flex-1 md:text-xs">
                    {link.label}
                  </span>
                  <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                </Link>
              ))}
            </div>
          </div>

          {/* Sessions Overview */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 lg:col-span-2">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-semibold text-gray-900 md:text-sm">
                Recent Sessions
              </h2>
              <Link
                to="/dashboard/mentor/sessions"
                className="text-[10px] text-orange-600 hover:underline md:text-xs"
              >
                View All
              </Link>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader className="w-5 h-5 text-orange-500 animate-spin" />
              </div>
            ) : sessions.length === 0 ? (
              <div className="text-center py-8">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-2 rounded-full bg-gradient-to-br from-gray-100 to-gray-200">
                  <Video className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-[10px] text-gray-500">No sessions yet</p>
                <Link
                  to="/mentorship"
                  className="inline-block mt-2 text-[10px] text-orange-600 hover:underline"
                >
                  Browse students
                </Link>
              </div>
            ) : (
              <div className="space-y-2">
                {sessions.slice(0, 5).map((session) => (
                  <div
                    key={session._id}
                    className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg"
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-orange-100 to-orange-200 rounded-full flex items-center justify-center overflow-hidden shrink-0">
                      {session.student?.avatar ? (
                        <img
                          src={session.student.avatar}
                          alt={session.student.name || "Student"}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <UserPlus className="w-4 h-4 text-orange-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-medium text-gray-900 truncate md:text-xs">
                        {session.student?.name || session.student?.firstName
                          ? `${session.student.firstName || ""} ${session.student.lastName || ""}`.trim()
                          : "Student"}
                      </p>
                      <p className="text-[9px] text-gray-500 truncate md:text-[10px]">
                        {session.topic}
                      </p>
                    </div>
                    <span
                      className={`text-[8px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap md:text-[9px] ${
                        session.status === "completed"
                          ? "bg-green-100 text-green-800"
                          : session.status === "confirmed"
                            ? "bg-blue-100 text-blue-800"
                            : session.status === "cancelled"
                              ? "bg-red-100 text-red-800"
                              : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {session.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Upcoming Sessions */}
        <div className="mt-5 bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <h2 className="text-xs font-semibold text-gray-900 mb-3 md:text-sm">
            Today's Schedule
          </h2>
          {upcomingSessions.length === 0 ? (
            <div className="text-center py-6">
              <Calendar className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-[10px] text-gray-500">No upcoming sessions today</p>
            </div>
          ) : (
            <div className="space-y-2">
              {upcomingSessions.map((session) => (
                <div
                  key={session._id}
                  className="flex flex-col gap-2 p-3 border border-gray-100 rounded-lg sm:flex-row sm:items-center sm:gap-3"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-orange-100 to-orange-200 rounded-full flex items-center justify-center overflow-hidden shrink-0">
                      {session.student?.avatar ? (
                        <img
                          src={session.student.avatar}
                          alt={session.student.name || "Student"}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <UserPlus className="w-5 h-5 text-orange-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-medium text-gray-900 md:text-xs">
                        {session.student?.name || session.student?.firstName
                          ? `${session.student.firstName || ""} ${session.student.lastName || ""}`.trim()
                          : "Student"}
                      </p>
                      <p className="text-[9px] text-gray-500 md:text-[10px]">
                        {session.topic}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 ml-13 sm:ml-0 sm:flex-1 sm:justify-end">
                    <span className="text-[9px] text-gray-500 md:text-[10px]">
                      {new Date(session.scheduledAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                    <span
                      className={`text-[8px] font-medium px-1.5 py-0.5 rounded-full md:text-[9px] ${
                        session.status === "completed"
                          ? "bg-green-100 text-green-800"
                          : session.status === "confirmed"
                            ? "bg-blue-100 text-blue-800"
                            : session.status === "cancelled"
                              ? "bg-red-100 text-red-800"
                              : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {session.status}
                    </span>
                    {session.meetingLink && session.status === "confirmed" && (
                      <a
                        href={session.meetingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-2 py-1 text-[9px] font-medium text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md whitespace-nowrap md:text-[10px]"
                      >
                        Join Meeting
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Stats Summary Footer */}
        <div className="grid grid-cols-3 gap-3 mt-5">
          <div className="bg-white rounded-xl p-2 text-center border border-gray-100 shadow-sm">
            <CheckCircle className="w-4 h-4 mx-auto mb-0.5 text-green-500" />
            <p className="text-[10px] font-bold text-gray-900">
              {completedSessions.length}
            </p>
            <p className="text-[7px] text-gray-500">Completed</p>
          </div>
          <div className="bg-white rounded-xl p-2 text-center border border-gray-100 shadow-sm">
            <Clock className="w-4 h-4 mx-auto mb-0.5 text-yellow-500" />
            <p className="text-[10px] font-bold text-gray-900">
              {pendingSessions.length}
            </p>
            <p className="text-[7px] text-gray-500">Pending</p>
          </div>
          <div className="bg-white rounded-xl p-2 text-center border border-gray-100 shadow-sm">
            <Calendar className="w-4 h-4 mx-auto mb-0.5 text-blue-500" />
            <p className="text-[10px] font-bold text-gray-900">
              {upcomingSessions.length}
            </p>
            <p className="text-[7px] text-gray-500">Upcoming</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MentorDashboardPage;